import type { Seat } from "@bing/engine-core";
import {
  FINAL_STEP,
  MENSCH_SEATS,
  RELEASE_ROLL,
  START_SPACING,
  TOKENS_PER_PLAYER,
  TRACK_LEN,
  YARD,
  type MenschConfig,
  type MenschState,
  type MenschStep,
} from "./types";

/* The pure rule kernel. Every function here is total and side-effect free, so the
 * engine, the bot and the tests all reason about exactly the same logic. */

/**
 * The board ARM a seat occupies.
 *
 * Seats are always `0..playerCount-1` (so they match the room's seat indices), but they
 * do not have to sit on consecutive arms. With TWO players they sit OPPOSITE each other
 * — arms 0 and 2 — the way two people actually sit at a real board; three and four take
 * arms in order. The race length is 40 either way, so this is presentation and capture
 * geometry, never an advantage.
 */
export function armOf(seat: Seat, playerCount: number): number {
  return playerCount === 2 ? seat * 2 : seat;
}

/** The absolute track square a seat enters on. Four symmetric arms → 10 apart. */
export function startSquare(seat: Seat, playerCount: number = MENSCH_SEATS): number {
  return armOf(seat, playerCount) * START_SPACING;
}

/**
 * The absolute track square a token occupies, or `null` when it is not on the
 * track (in the yard, or in a home row — home rows are private, so they have no
 * absolute square and can never be shared).
 */
export function absoluteSquare(
  seat: Seat,
  step: MenschStep,
  playerCount: number = MENSCH_SEATS,
): number | null {
  if (step < 0 || step >= TRACK_LEN) return null;
  return (startSquare(seat, playerCount) + step) % TRACK_LEN;
}

/** A token that has reached the end of its home row. */
export function isFinished(step: MenschStep): boolean {
  return step === FINAL_STEP;
}

/** Tokens this seat has landed in its home row (steps 40..43). */
export function tokensHome(state: MenschState, seat: Seat): number {
  return (state.tokens[seat] ?? []).filter((s) => s >= TRACK_LEN).length;
}

/** A seat wins by getting ALL of its tokens into the home row. */
export function hasWon(state: MenschState, seat: Seat): boolean {
  return tokensHome(state, seat) === TOKENS_PER_PLAYER;
}

/** The fresh board: every token in its yard. One row per SEATED player. */
export function setupTokens(playerCount: number = MENSCH_SEATS): MenschStep[][] {
  return Array.from({ length: playerCount }, () =>
    Array.from({ length: TOKENS_PER_PLAYER }, () => YARD),
  );
}

/**
 * Where `token` would land with `die`, or `null` if that is not a legal move.
 *
 * Enforces, in order:
 *  - R5  a yard token needs exactly a 6, and lands on its start square;
 *  - R10 an exact roll is required to finish — overshooting 43 is not a move;
 *  - R8  you may never land on your OWN token (must move a different one).
 *
 * Capture (R7) is NOT decided here — it is a consequence of the destination, see
 * {@link captureAt}.
 */
export function destinationFor(
  state: MenschState,
  seat: Seat,
  token: number,
  die: number,
): MenschStep | null {
  const mine = state.tokens[seat];
  if (!mine) return null;
  const step = mine[token];
  if (step === undefined) return null;
  if (isFinished(step)) return null; // already home and done

  let target: MenschStep;
  if (step === YARD) {
    // R5 — only a 6 releases, onto the start square.
    if (die !== RELEASE_ROLL) return null;
    target = 0;
  } else {
    target = step + die;
    // R10 — exact roll to finish; you may not overshoot the final home square.
    if (target > FINAL_STEP) return null;
  }

  // R8 — your own token blocks the square (this also prevents self-stacking, which
  // is why no "blocking/barrier" rule can arise in منچ).
  if (mine.some((s, i) => i !== token && s === target)) return null;

  // R13 — an opponent's OWN start square, while they are standing on it, is not a legal
  // destination: they cannot be captured there, and two tokens can never share a square.
  // Your own start square is not blocked FOR YOU by this (R8 already governs that), and
  // passing OVER a protected square is unaffected — only landing on it is refused.
  const square = absoluteSquare(seat, target, state.tokens.length);
  if (square !== null && startSquare(seat, state.tokens.length) !== square) {
    if (isProtectedStart(state, square)) return null;
  }

  return target;
}

/**
 * Is `square` the protected start square of a seat that currently has a token standing on it?
 *
 * R13 — a token on its OWN start square is safe. `step === 0` is "on my start square" in the
 * position model, which is exactly why the model was built this way.
 */
export function isProtectedStart(state: MenschState, square: number): boolean {
  if (!state.config.safeStartSquare) return false;
  const players = state.tokens.length;
  for (let s = 0; s < players; s += 1) {
    if (startSquare(s, players) !== square) continue;
    // The owner is home only if one of ITS tokens is actually sitting there.
    if ((state.tokens[s] ?? []).some((step) => step === 0)) return true;
  }
  return false;
}

/**
 * The opponent token standing on the square `seat` would land on, if any.
 * Only track squares can be contested — a home row is private by construction.
 */
export function captureAt(
  state: MenschState,
  seat: Seat,
  target: MenschStep,
): { seat: Seat; token: number } | null {
  const players = state.tokens.length;
  const square = absoluteSquare(seat, target, players);
  if (square === null) return null;
  for (let s = 0; s < players; s += 1) {
    if (s === seat) continue;
    const theirs = state.tokens[s];
    if (!theirs) continue;
    for (let t = 0; t < theirs.length; t += 1) {
      const step = theirs[t];
      if (step === undefined) continue;
      if (absoluteSquare(s, step, players) === square) return { seat: s, token: t };
    }
  }
  return null;
}

/**
 * Every token index this seat may legally move with `die`.
 *
 * R9 — when `mandatoryCapture` is on and any move would capture, ONLY the
 * capturing moves are legal («Schmeißen ist Pflicht»).
 */
export function legalTokens(state: MenschState, seat: Seat, die: number): number[] {
  const all: number[] = [];
  const capturing: number[] = [];
  const mine = state.tokens[seat];
  if (!mine) return all;
  for (let t = 0; t < mine.length; t += 1) {
    const target = destinationFor(state, seat, t, die);
    if (target === null) continue;
    all.push(t);
    if (captureAt(state, seat, target)) capturing.push(t);
  }
  if (state.config.mandatoryCapture && capturing.length > 0) return capturing;
  return all;
}

/** True when every one of this seat's tokens is still in the yard (drives V1). */
export function yardLocked(state: MenschState, seat: Seat): boolean {
  return (state.tokens[seat] ?? []).every((s) => s === YARD);
}

/**
 * May this seat roll again after an unproductive roll? Only while yard-locked, and
 * only up to `yardRollAttempts` (V1). A player with a token on the board gets the
 * single roll the core rules give them.
 */
export function mayRollAgain(state: MenschState, seat: Seat, config: MenschConfig): boolean {
  return yardLocked(state, seat) && state.rollsUsed < config.yardRollAttempts;
}

/** The next seat to act (clockwise), among the seats actually playing. */
export function nextSeat(seat: Seat, playerCount: number = MENSCH_SEATS): Seat {
  return (seat + 1) % playerCount;
}
